import { useEffect, useMemo, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Edit2, RotateCcw, Wrench, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useAITaskConfigs,
  useUpdateAITaskConfig,
  useResetAITaskConfig,
  useTestAITaskOnSample,
  type AITaskConfig,
  type AITaskMode,
  type FactcheckLeniency,
  type UpdateAITaskConfigInput,
} from '@/hooks/useAITaskConfigs'
import { useAIProviders } from '@/hooks/useAIProviders'
import {
  useAIProviderModels,
  type ProviderName,
} from '@/hooks/useAIProviderModels'
import { AdminAuditFeed, type AuditEntry } from '@/components/admin/AdminAuditFeed'

// ─── Provider lookup helpers ──────────────────────────────────────────────────

interface ProviderOption {
  id: string
  name: string
}

/** Map a provider name (what task-config rows carry) to its uuid. */
function providerIdByName(
  name: string | undefined,
  providers: ProviderOption[],
): string | undefined {
  if (!name) return undefined
  return providers.find((p) => p.name === name)?.id
}

/**
 * Pull the catalogue array for a single provider out of the response shape
 * returned by `useAIProviderModels`. The `catalogue` record may hold either
 * an array (OpenRouter live) or an object (fallback), but in practice the
 * admin-providers route returns arrays across all five providers.
 */
function catalogueFor(
  provider: string | undefined,
  hookData: ReturnType<typeof useAIProviderModels>['data'],
): Array<{ id: string; label?: string }> {
  if (!provider || !hookData?.catalogue) return []
  const entry = hookData.catalogue[provider as ProviderName]
  if (Array.isArray(entry)) {
    return entry as Array<{ id: string; label?: string }>
  }
  return []
}

// ─── Leg editor ───────────────────────────────────────────────────────────────

interface Leg {
  provider: string
  model: string
}

interface LegEditorProps {
  label: string
  value: Leg
  providers: ProviderOption[]
  onChange: (next: Leg) => void
  required?: boolean
  error?: string
  idPrefix: string
}

function LegEditor({
  label,
  value,
  providers,
  onChange,
  required,
  error,
  idPrefix,
}: LegEditorProps) {
  const modelsQ = useAIProviderModels(
    value.provider ? (value.provider as ProviderName) : undefined,
  )
  const models = useMemo(
    () => catalogueFor(value.provider, modelsQ.data),
    [value.provider, modelsQ.data],
  )

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${idPrefix}-provider`} className="text-sm font-medium">
          {label} Provider{required && <span className="text-red-400"> *</span>}
        </label>
        <Select
          value={value.provider}
          onValueChange={(v) => onChange({ provider: v, model: '' })}
        >
          <SelectTrigger id={`${idPrefix}-provider`} aria-label={`${label} Provider`}>
            <SelectValue placeholder="Select provider" />
          </SelectTrigger>
          <SelectContent>
            {providers.map((p) => (
              <SelectItem key={p.id} value={p.name}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${idPrefix}-model`} className="text-sm font-medium">
          {label} Model{required && <span className="text-red-400"> *</span>}
        </label>
        {models.length > 0 ? (
          <Select
            value={value.model}
            onValueChange={(v) => onChange({ ...value, model: v })}
          >
            <SelectTrigger id={`${idPrefix}-model`} aria-label={`${label} Model`}>
              <SelectValue placeholder="Select model" />
            </SelectTrigger>
            <SelectContent>
              {models.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.label ?? m.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id={`${idPrefix}-model`}
            value={value.model}
            onChange={(e) => onChange({ ...value, model: e.target.value })}
            placeholder="Model id"
          />
        )}
      </div>
      {error && (
        <p className="col-span-2 text-xs text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

// ─── Test-on-sample panel ─────────────────────────────────────────────────────

interface TestPanelProps {
  taskName: string
  onClose: () => void
}

function TestPanel({ taskName, onClose }: TestPanelProps) {
  const [sampleInput, setSampleInput] = useState('')
  const [result, setResult] = useState<{ ok: boolean; output?: unknown; error?: string } | null>(
    null,
  )
  const testM = useTestAITaskOnSample()
  const running = testM.isPending

  async function handleRun() {
    setResult(null)
    try {
      const res = await testM.mutateAsync({ taskName, sampleInput })
      setResult(res)
    } catch (err) {
      setResult({ ok: false, error: err instanceof Error ? err.message : String(err) })
    }
  }

  return (
    <DialogPrimitive.Root open onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-[60] w-full max-w-xl -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background shadow-xl"
          aria-describedby="test-panel-description"
        >
          <div className="flex items-start justify-between gap-4 border-b px-6 py-4">
            <div>
              <DialogPrimitive.Title className="text-base font-semibold">
                Test {taskName} on Sample
              </DialogPrimitive.Title>
              <DialogPrimitive.Description
                id="test-panel-description"
                className="text-xs text-muted-foreground mt-0.5"
              >
                Runs the current (in-DB) task config through the live AIRouter.
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </DialogPrimitive.Close>
          </div>
          <div className="flex flex-col gap-3 px-6 py-4">
            <label htmlFor="sample-input" className="text-sm font-medium">
              Sample input
            </label>
            <textarea
              id="sample-input"
              value={sampleInput}
              onChange={(e) => setSampleInput(e.target.value)}
              rows={6}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y"
              placeholder="Paste sample article text here…"
            />
            {result && (
              <pre className="max-h-64 overflow-auto rounded-md border bg-muted/20 px-3 py-2 text-xs font-mono">
                {JSON.stringify(result, null, 2)}
              </pre>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t px-6 py-4">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={handleRun} disabled={running || !sampleInput.trim()}>
              {running ? 'Running…' : 'Run'}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

// ─── Edit modal ───────────────────────────────────────────────────────────────

interface EditModalProps {
  task: AITaskConfig | null
  providers: ProviderOption[]
  onClose: () => void
}

function EditModal({ task, providers, onClose }: EditModalProps) {
  const [mode, setMode] = useState<AITaskMode>('single')
  const [primary, setPrimary] = useState<Leg>({ provider: '', model: '' })
  const [secondary, setSecondary] = useState<Leg>({ provider: '', model: '' })
  const [fallback, setFallback] = useState<Leg>({ provider: '', model: '' })
  const [promptTemplate, setPromptTemplate] = useState('')
  const [confidenceGate, setConfidenceGate] = useState('')
  const [leniency, setLeniency] = useState<FactcheckLeniency>('strict')
  const [testingOpen, setTestingOpen] = useState(false)

  // Prefill whenever the target task changes.
  useEffect(() => {
    if (!task) return
    setMode(task.mode)
    setPrimary({
      provider: task.primary.providerName,
      model: task.primary.model,
    })
    setSecondary(
      task.secondary
        ? { provider: task.secondary.providerName, model: task.secondary.model }
        : { provider: '', model: '' },
    )
    setFallback(
      task.fallback
        ? { provider: task.fallback.providerName, model: task.fallback.model }
        : { provider: '', model: '' },
    )
    setPromptTemplate(task.promptTemplate ?? '')
    setConfidenceGate(
      task.confidenceGateThreshold != null ? String(task.confidenceGateThreshold) : '',
    )
    setLeniency(task.factcheckLeniency ?? 'strict')
  }, [task])

  const updateM = useUpdateAITaskConfig()

  const primaryOk = Boolean(primary.provider && primary.model)
  const needsSecondary = mode !== 'single'
  const secondaryOk = !needsSecondary || Boolean(secondary.provider && secondary.model)
  const fallbackPresent = Boolean(fallback.provider || fallback.model)
  const fallbackOk = !fallbackPresent || Boolean(fallback.provider && fallback.model)

  const canSave = primaryOk && secondaryOk && fallbackOk && !updateM.isPending

  function handleSave() {
    if (!task || !canSave) return
    const primaryId = providerIdByName(primary.provider, providers)
    if (!primaryId) return // guard — UI disables Save without a provider, but belt-and-braces
    const body: UpdateAITaskConfigInput = {
      mode,
      primary: { providerId: primaryId, model: primary.model },
      promptTemplate: promptTemplate.trim() ? promptTemplate : null,
    }
    if (needsSecondary) {
      const secondaryId = providerIdByName(secondary.provider, providers)
      if (!secondaryId) return
      body.secondary = { providerId: secondaryId, model: secondary.model }
    }
    if (fallbackPresent) {
      const fallbackId = providerIdByName(fallback.provider, providers)
      if (!fallbackId) return
      body.fallback = { providerId: fallbackId, model: fallback.model }
    }
    if (mode === 'consensus' && confidenceGate.trim()) {
      const v = Number(confidenceGate)
      if (!Number.isNaN(v) && v >= 0 && v <= 1) body.confidenceGateThreshold = v
    }
    if (mode === 'fact_check') {
      body.factcheckLeniency = leniency
    }
    updateM.mutate(
      { taskName: task.taskName, ...body },
      { onSuccess: () => onClose() },
    )
  }

  if (!task) return null

  return (
    <DialogPrimitive.Root open onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-xl -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background shadow-xl max-h-[90vh] overflow-y-auto"
          aria-describedby="edit-task-description"
        >
          <div className="flex items-start justify-between gap-4 border-b px-6 py-4">
            <div>
              <DialogPrimitive.Title className="text-base font-semibold">
                Configure task: {task.taskName}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description
                id="edit-task-description"
                className="text-xs text-muted-foreground mt-0.5"
              >
                Select providers, model, mode, and optional prompt template.
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </DialogPrimitive.Close>
          </div>

          <div className="flex flex-col gap-4 px-6 py-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="task-mode" className="text-sm font-medium">
                Mode
              </label>
              <Select value={mode} onValueChange={(v) => setMode(v as AITaskMode)}>
                <SelectTrigger id="task-mode" aria-label="Mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">single</SelectItem>
                  <SelectItem value="consensus">consensus</SelectItem>
                  <SelectItem value="fact_check">fact_check</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <LegEditor
              label="Primary"
              value={primary}
              providers={providers}
              onChange={setPrimary}
              required
              idPrefix="primary"
              error={!primaryOk ? 'Primary provider and model are required.' : undefined}
            />

            {needsSecondary && (
              <LegEditor
                label="Secondary"
                value={secondary}
                providers={providers}
                onChange={setSecondary}
                required
                idPrefix="secondary"
                error={!secondaryOk ? 'Secondary provider and model are required.' : undefined}
              />
            )}

            <div className="flex flex-col gap-1.5">
              <p className="text-xs text-muted-foreground">
                Fallback (optional — used when the primary call fails)
              </p>
              <LegEditor
                label="Fallback"
                value={fallback}
                providers={providers}
                onChange={setFallback}
                idPrefix="fallback"
                error={!fallbackOk ? 'If one fallback field is set, both are required.' : undefined}
              />
            </div>

            {mode === 'consensus' && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="confidence-gate" className="text-sm font-medium">
                  Confidence gate threshold (0–1)
                </label>
                <Input
                  id="confidence-gate"
                  type="number"
                  step="0.05"
                  min={0}
                  max={1}
                  value={confidenceGate}
                  onChange={(e) => setConfidenceGate(e.target.value)}
                  placeholder="e.g. 0.7"
                />
              </div>
            )}

            {mode === 'fact_check' && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="leniency" className="text-sm font-medium">
                  Fact-check leniency
                </label>
                <Select
                  value={leniency}
                  onValueChange={(v) => setLeniency(v as FactcheckLeniency)}
                >
                  <SelectTrigger id="leniency" aria-label="Leniency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strict">strict</SelectItem>
                    <SelectItem value="permissive">permissive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label htmlFor="prompt-template" className="text-sm font-medium">
                Prompt template
              </label>
              <textarea
                id="prompt-template"
                aria-label="Prompt template"
                value={promptTemplate}
                onChange={(e) => setPromptTemplate(e.target.value)}
                rows={6}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y"
                placeholder="Optional — overrides the code default prompt."
              />
            </div>

            {updateM.isError && (
              <p className="text-xs text-red-400" role="alert">
                Save failed: {String((updateM.error as Error)?.message ?? 'unknown error')}
              </p>
            )}
          </div>

          <div className="flex justify-between gap-2 border-t px-6 py-4">
            <Button variant="outline" onClick={() => setTestingOpen(true)}>
              Test on Sample
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!canSave}>
                Save
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>

      {testingOpen && (
        <TestPanel taskName={task.taskName} onClose={() => setTestingOpen(false)} />
      )}
    </DialogPrimitive.Root>
  )
}

// ─── Reset confirm ────────────────────────────────────────────────────────────

interface ResetConfirmProps {
  taskName: string
  onCancel: () => void
  onConfirmed: () => void
}

function ResetConfirm({ taskName, onCancel, onConfirmed }: ResetConfirmProps) {
  const resetM = useResetAITaskConfig()

  function handleConfirm() {
    resetM.mutate(taskName, {
      onSuccess: () => onConfirmed(),
    })
  }

  return (
    <DialogPrimitive.Root open onOpenChange={(o) => !o && onCancel()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[55] bg-slate-950/60 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-[55] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background shadow-xl"
          aria-describedby="reset-confirm-description"
        >
          <div className="px-6 py-4 border-b">
            <DialogPrimitive.Title className="text-base font-semibold">
              Reset task config
            </DialogPrimitive.Title>
          </div>
          <DialogPrimitive.Description
            id="reset-confirm-description"
            className="px-6 py-4 text-sm"
          >
            Reset task <code className="font-mono">{taskName}</code> to its code default?
            The current database override will be deleted.
          </DialogPrimitive.Description>
          {resetM.isError && (
            <p className="px-6 pb-2 text-xs text-red-400" role="alert">
              Reset failed: {String((resetM.error as Error)?.message ?? 'unknown error')}
            </p>
          )}
          <div className="flex justify-end gap-2 border-t px-6 py-4">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={resetM.isPending}
            >
              {resetM.isPending ? 'Resetting…' : 'Confirm reset'}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

// ─── Tasks table ──────────────────────────────────────────────────────────────

interface TasksTableProps {
  tasks: AITaskConfig[]
  onEdit: (task: AITaskConfig) => void
  onResetRequest: (taskName: string) => void
}

function formatUpdatedAt(iso: string | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

function TasksTable({ tasks, onEdit, onResetRequest }: TasksTableProps) {
  return (
    <div className="rounded-lg border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/20 text-muted-foreground">
              <th className="px-4 py-3 text-left font-medium">Task</th>
              <th className="px-4 py-3 text-left font-medium">Mode</th>
              <th className="px-4 py-3 text-left font-medium">Primary</th>
              <th className="px-4 py-3 text-left font-medium hidden md:table-cell">Status</th>
              <th className="px-4 py-3 text-left font-medium hidden lg:table-cell">Updated</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <tr
                key={task.taskName}
                className="border-b last:border-0 hover:bg-muted/30 transition-colors"
              >
                <td className="px-4 py-3">
                  <span className="font-mono text-xs">{task.taskName}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs capitalize text-muted-foreground">{task.mode}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs">
                    {task.primary.providerName} /{' '}
                    <span className="font-mono">{task.primary.model}</span>
                  </span>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  {task.overridden ? (
                    <span className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-300">
                      overridden
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full border border-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      code default
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <span className="text-xs text-muted-foreground">
                    {formatUpdatedAt(task.updatedAt)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {task.overridden && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => onResetRequest(task.taskName)}
                        title={`Reset ${task.taskName} to code default`}
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1" />
                        Reset
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={() => onEdit(task)}
                      title={`Edit ${task.taskName}`}
                      aria-label={`Edit ${task.taskName}`}
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Audit feed formatting ────────────────────────────────────────────────────

const AUDIT_ACTION_LABELS: Record<string, string> = {
  task_config_update: 'Updated task config',
  task_config_reset: 'Reset task config to code default',
  provider_enable: 'Enabled provider',
  provider_disable: 'Disabled provider',
  provider_base_url_update: 'Updated provider base URL',
  key_rotate: 'Rotated provider key',
  key_test: 'Tested provider key',
}

function renderAuditAction(entry: AuditEntry): string {
  const label = AUDIT_ACTION_LABELS[entry.action] ?? entry.action
  return `${label} — ${entry.targetName}`
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AITasksPage() {
  const tasksQ = useAITaskConfigs()
  const providersQ = useAIProviders()

  const providers: ProviderOption[] = useMemo(() => {
    const rows = providersQ.data?.providers ?? []
    return rows
      .filter((p) => p.enabled !== false)
      .map((p) => ({ id: p.id, name: p.name }))
  }, [providersQ.data])

  const [editingTask, setEditingTask] = useState<AITaskConfig | null>(null)
  const [resetTargetName, setResetTargetName] = useState<string | null>(null)

  const tasks = tasksQ.data?.tasks ?? []
  const sortedTasks = useMemo(
    () => [...tasks].sort((a, b) => a.taskName.localeCompare(b.taskName)),
    [tasks],
  )

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Wrench className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">AI Task Configuration</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Map pipeline tasks to AI providers, models, and prompt templates.
          </p>
        </div>
      </div>

      {tasksQ.isLoading && (
        <div className="text-sm text-muted-foreground">Loading tasks…</div>
      )}
      {tasksQ.isError && (
        <div className="text-sm text-red-400" role="alert">
          Failed to load tasks: {String((tasksQ.error as Error)?.message ?? 'unknown')}
        </div>
      )}

      {!tasksQ.isLoading && !tasksQ.isError && (
        <TasksTable
          tasks={sortedTasks}
          onEdit={setEditingTask}
          onResetRequest={setResetTargetName}
        />
      )}

      {/* Audit feed (all router-admin actions, server-filtered for brevity) */}
      <div className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold tracking-tight">Recent admin activity</h2>
        <AdminAuditFeed feedUrl="/admin/router/audit" renderAction={renderAuditAction} />
      </div>

      {/* Edit modal */}
      {editingTask && (
        <EditModal
          task={editingTask}
          providers={providers}
          onClose={() => setEditingTask(null)}
        />
      )}

      {/* Reset confirm modal */}
      {resetTargetName && (
        <ResetConfirm
          taskName={resetTargetName}
          onCancel={() => setResetTargetName(null)}
          onConfirmed={() => setResetTargetName(null)}
        />
      )}
    </div>
  )
}
